import { getValueFromPath } from '@/helpers/pathHelper';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { GeneratorTechnology, GeneratorTypeRef } from '@/dto/scenario/generator/GeneratorReference';
import { BatteryTypeRef } from '@/dto/scenario/battery/BatteryReference';
import { Specification } from '@/services/ruleService';

type Field = string;
type Value = number | string;
export type Preset<EnumType = Record<string, unknown>, FieldValue = DefaultValue<Value> | null> = {
  [P in keyof EnumType]: Record<Field, FieldValue>
}

interface Dependencies {
  loadFile: <FileContent>(name: string) => Promise<{data: FileContent}>;
  loadRules: () => Promise<unknown>;
}

// TODO migrate the rest of this service inside references store module
/*
 * @deprecated Use references store module instead
 */
export const buildReferencesService = (dependencies: Dependencies) => {
  const { loadFile, loadRules } = dependencies;
  const loadedPresets: Record<string, Preset> = {};

  async function getPresets<EnumType extends Record<string, unknown>>(filename: string): Promise<Preset<EnumType>> {
    if (loadedPresets[filename] === undefined) {
      const { data: presets } = await loadFile<Preset<EnumType, Value | null>>(filename);
      const fieldRefs = await loadRules();
      loadedPresets[filename] = computeDefaultValuesForPresets(presets, fieldRefs);
    }
    return loadedPresets[filename] as Preset<EnumType>;
  }

  function computeDefaultValuesForPresets<EnumType extends Record<string, unknown>>(presets: Preset<EnumType, Value | null>, specs: unknown): Preset<EnumType> {
    return Object.entries(presets)
      .reduce((accumulator, [enumKey, preset]) => ({
        ...accumulator,
        [enumKey]: Object.entries(preset)
          .reduce((accumulator: Record<Field, DefaultValue<Value> | null>, [fieldPath, presetValue]) => {
            return {
              ...accumulator,
              [fieldPath]: presetValue !== null ? computeDefaultValue(specs, fieldPath, presetValue) : null
            };
          }, {})
      }), {} as Preset<EnumType>);
  }

  function computeDefaultValue(specs: unknown, field: string, presetValue: Value) {
    const fieldSpecs = getValueFromPath(specs, field.split('.')) as Specification;
    return new DefaultValue(presetValue)
      .withLock(fieldSpecs.protected ?? false);
  }

  return {
    getPVRef: async(): Promise<Preset> => getPresets('pv_references'),
    getBatteryRef: async(): Promise<Preset<typeof BatteryTypeRef>> => getPresets('battery_references'),
    getGeneratorRefs: async(): Promise<Preset<typeof GeneratorTypeRef>> => getPresets('generator_references'),
    getFuelRefs: async(): Promise<Preset<typeof GeneratorTechnology>> => getPresets('fuel_references'),
    getInflationRef: async(): Promise<Preset> => getPresets('inflation_references'),
    getFxRef: async(): Promise<Preset> => getPresets('fx_references'),
    getFdRef: async(): Promise<Preset> => getPresets('fd_thresholds_references')
  };
};
